import React, { useState, useEffect, useId } from 'react';

import { useTranslation } from 'react-i18next';
import { useModalContext } from './ModalProvider';
import { userAPI } from '../UserAPI';

import { AlertTriangle, Info, Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface StripeAccountFormProps {
    customerId: string;
    onSuccess?: (accountData: any) => void;
    onCancel?: () => void;
}

interface AccountData {
    business_type: 'individual' | 'company';
    company?: {
        name: string;
        tax_id?: string;
    };
    individual?: {
        first_name: string;
        last_name: string;
        email: string;
        phone?: string;
        dob?: {
            day: number;
            month: number;
            year: number;
        };
    };
    business_profile?: {
        url?: string;
        mcc?: string;
    };
    capabilities?: {
        card_payments?: { requested: boolean };
        transfers?: { requested: boolean };
    };
}

interface StripeAccount {
    id: string;
    business_type: 'individual' | 'company';
    company?: {
        name: string;
        tax_id?: string;
    };
    individual?: {
        first_name: string;
        last_name: string;
        email: string;
        phone?: string;
    };
    business_profile?: {
        url?: string;
        mcc?: string;
    };
    charges_enabled: boolean;
    payouts_enabled: boolean;
    requirements: {
        currently_due: string[];
        eventually_due: string[];
        past_due: string[];
    };
}

/** Pflichtfeld-Stern wie in den übrigen Formularen. */
const RequiredMark = () => <span aria-hidden="true" className="ml-0.5 text-destructive">*</span>;

export default function StripeAccountForm({ customerId, onSuccess, onCancel: _onCancel }: StripeAccountFormProps) {
    const { t } = useTranslation();
    const { pushError } = useModalContext() as any;
    const fieldId = useId();
    const [loading, setLoading] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [isLinkingExisting, setIsLinkingExisting] = useState(false);
    const [existingAccountId, setExistingAccountId] = useState('');
    const [accountData, setAccountData] = useState<AccountData>({
        business_type: 'individual',
        individual: {
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
        },
        company: {
            name: '',
            tax_id: '',
        },
        business_profile: {
            url: '',
            mcc: '',
        },
        capabilities: {
            card_payments: { requested: true },
            transfers: { requested: true },
        },
    });
    const [stripeAccount, setStripeAccount] = useState<StripeAccount | null>(null);

    useEffect(() => {
        loadStripeAccount();
    }, [customerId]);

    const loadStripeAccount = async () => {
        try {
            const account = await userAPI.getStripeAccount(customerId);
            setStripeAccount(account);
        } catch (error) {
            // Account doesn't exist yet, which is fine
            console.log('No existing Stripe account found');
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);

        try {
            let result = await userAPI.createStripeAccount(customerId, accountData);
            //open a new tab to the redirect uri
            window.open(result.url, '_blank');
            setStripeAccount(result);
            setShowDialog(false);
            onSuccess?.(result);
        } catch (error) {
            console.error('Error with Stripe account:', error);
            pushError(error instanceof Error ? error : new Error(isLinkingExisting ? 'Failed to link Stripe account' : 'Failed to create Stripe account'));
        } finally {
            setLoading(false);
        }
    };

    const updateAccountData = (field: string, value: any) => {
        setAccountData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const updateIndividual = (field: string, value: any) => {
        setAccountData(prev => ({
            ...prev,
            individual: {
                ...prev.individual!,
                [field]: value
            }
        }));
    };

    const updateCompany = (field: string, value: any) => {
        setAccountData(prev => ({
            ...prev,
            company: {
                ...prev.company!,
                [field]: value
            }
        }));
    };

    const updateBusinessProfile = (field: string, value: any) => {
        setAccountData(prev => ({
            ...prev,
            business_profile: {
                ...prev.business_profile,
                [field]: value
            }
        }));
    };

    const _translateRequirement = (requirement: string): string => {
        return t(`stripe.requirements.${requirement}`, requirement);
    };

    const renderAccountData = () => {
        if (!stripeAccount) {
            return (
                <div className="flex min-w-0 flex-col gap-4">
                    <div className="text-sm">Dieses Konto wird gebraucht, um euch Zahlungen für die Nutzung eurer Dienste und Wissenbasen zu überweisen.</div>
                    <Alert variant="info">
                        <Info aria-hidden className="h-4 w-4" />
                        <AlertDescription>
                            Kein Stripe-Konto konfiguriert. Erstelle ein neues Konto oder verknüpfe ein bestehendes Stripe Konto.
                        </AlertDescription>
                    </Alert>
                    <div className="flex flex-wrap gap-3">
                        <Button
                            type="button"
                            onClick={() => {
                                setIsLinkingExisting(false);
                                setShowDialog(true);
                            }}
                        >
                            Zahlungskonto anlegen
                        </Button>
                    </div>
                </div>
            );
        }

        return (
            <div className="flex min-w-0 flex-col gap-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-lg font-bold">
                        {stripeAccount.business_type === 'individual'
                            ? `${stripeAccount.individual?.first_name} ${stripeAccount.individual?.last_name}`
                            : stripeAccount.company?.name
                        }
                    </span>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => window.open(`https://connect.stripe.com/app/express#${stripeAccount.id}`, '_blank')}
                    >
                        Zum Stripe Dashboard
                    </Button>
                </div>

                <div className="flex flex-col gap-2">
                    <span className="text-sm font-bold">Kontostatus</span>
                    <div className="flex flex-wrap gap-4">
                        <span className="text-sm">
                            Zahlungen: {stripeAccount.charges_enabled ? 'Aktiviert' : 'Deaktiviert'}
                        </span>
                        <span className="text-sm">
                            Auszahlungen: {stripeAccount.payouts_enabled ? 'Aktiviert' : 'Deaktiviert'}
                        </span>
                    </div>
                </div>

                {stripeAccount.requirements.currently_due.length > 0 && (
                    <Alert variant="warning">
                        <AlertTriangle aria-hidden className="h-4 w-4" />
                        <AlertDescription>
                            Es sind Maßnahmen erforderlich, um das Zahlungskonto zu aktivieren. Gehen Sie bitte in Ihr Stripe Dashboard und füllen Sie die erforderlichen Informationen aus.
                        </AlertDescription>
                    </Alert>
                )}

                <div className="flex flex-col gap-2">
                    <span className="text-sm font-bold">Kontaktdaten</span>
                    {stripeAccount.business_type === 'individual' ? (
                        <div className="flex flex-col gap-1">
                            <span className="text-sm">E-Mail: {stripeAccount.individual?.email}</span>
                            {stripeAccount.individual?.phone && (
                                <span className="text-sm">Telefon: {stripeAccount.individual.phone}</span>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-1">
                            {stripeAccount.company?.tax_id && (
                                <span className="text-sm">Steuernummer: {stripeAccount.company.tax_id}</span>
                            )}
                        </div>
                    )}
                </div>

                {stripeAccount.business_profile?.url && (
                    <div className="flex flex-col gap-2">
                        <span className="text-sm font-bold">Website</span>
                        <span className="text-sm">{stripeAccount.business_profile.url}</span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {renderAccountData()}

            <Dialog
                open={showDialog}
                onOpenChange={(open) => {
                    if (!open) {
                        setShowDialog(false);
                    }
                }}
            >
                <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[800px]">
                    <DialogHeader>
                        <DialogTitle>{isLinkingExisting ? 'Bestehendes Stripe Konto verknüpfen' : 'Stripe Konto erstellen'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit}>
                        <div className="flex min-w-0 flex-col gap-4">
                            {isLinkingExisting ? (
                                <>
                                    <Alert variant="info">
                                        <Info aria-hidden className="h-4 w-4" />
                                        <AlertDescription>
                                            Geben Sie Ihre Stripe Account ID ein, um ein bestehendes Konto zu verknüpfen.
                                            Sie finden diese in Ihrem Stripe Dashboard unter Einstellungen → Account Details.
                                        </AlertDescription>
                                    </Alert>
                                    <div className="flex flex-col gap-1.5">
                                        <Label htmlFor={`${fieldId}-accountId`}>Stripe Account ID<RequiredMark /></Label>
                                        <Input
                                            id={`${fieldId}-accountId`}
                                            placeholder="acct_..."
                                            value={existingAccountId}
                                            onChange={(event) => setExistingAccountId(event.target.value || '')}
                                            aria-describedby={`${fieldId}-accountId-description`}
                                        />
                                        <p id={`${fieldId}-accountId-description`} className="text-sm text-muted-foreground">
                                            Ihre Stripe Account ID (beginnt mit 'acct_')
                                        </p>
                                    </div>
                                </>
                            ) : (
                                <Alert variant="info">
                                    <Info aria-hidden className="h-4 w-4" />
                                    <AlertDescription>
                                        Um Zahlungen über den Marktplatz zu erhalten, benötigen wir Ihre Kontodaten für Stripe Connect.
                                        Diese werden sicher an Stripe übertragen und für die Auszahlung von Einnahmen verwendet.
                                    </AlertDescription>
                                </Alert>
                            )}

                            <div className="flex flex-wrap gap-4">
                                <Button
                                    type="button"
                                    variant={accountData.business_type === 'individual' ? 'default' : 'outline'}
                                    aria-pressed={accountData.business_type === 'individual'}
                                    onClick={() => updateAccountData('business_type', 'individual')}
                                >
                                    Privatperson
                                </Button>
                                <Button
                                    type="button"
                                    variant={accountData.business_type === 'company' ? 'default' : 'outline'}
                                    aria-pressed={accountData.business_type === 'company'}
                                    onClick={() => updateAccountData('business_type', 'company')}
                                >
                                    Unternehmen
                                </Button>
                            </div>

                            {accountData.business_type === 'individual' ? (
                                <div className="flex flex-col gap-3">
                                    <span className="text-sm font-bold">Persönliche Daten</span>
                                    <div className="flex flex-wrap gap-3">
                                        <div className="flex min-w-0 flex-1 basis-56 flex-col gap-1.5">
                                            <Label htmlFor={`${fieldId}-firstName`}>Vorname<RequiredMark /></Label>
                                            <Input
                                                id={`${fieldId}-firstName`}
                                                value={accountData.individual?.first_name || ''}
                                                onChange={(event) => updateIndividual('first_name', event.target.value || '')}
                                            />
                                        </div>
                                        <div className="flex min-w-0 flex-1 basis-56 flex-col gap-1.5">
                                            <Label htmlFor={`${fieldId}-lastName`}>Nachname<RequiredMark /></Label>
                                            <Input
                                                id={`${fieldId}-lastName`}
                                                value={accountData.individual?.last_name || ''}
                                                onChange={(event) => updateIndividual('last_name', event.target.value || '')}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <Label htmlFor={`${fieldId}-email`}>E-Mail<RequiredMark /></Label>
                                        <Input
                                            id={`${fieldId}-email`}
                                            type="email"
                                            value={accountData.individual?.email || ''}
                                            onChange={(event) => updateIndividual('email', event.target.value || '')}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <Label htmlFor={`${fieldId}-phone`}>Telefon (optional)</Label>
                                        <Input
                                            id={`${fieldId}-phone`}
                                            value={accountData.individual?.phone || ''}
                                            onChange={(event) => updateIndividual('phone', event.target.value || '')}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-3">
                                    <span className="text-sm font-bold">Unternehmensdaten</span>
                                    <div className="flex flex-col gap-1.5">
                                        <Label htmlFor={`${fieldId}-companyName`}>Firmenname<RequiredMark /></Label>
                                        <Input
                                            id={`${fieldId}-companyName`}
                                            value={accountData.company?.name || ''}
                                            onChange={(event) => updateCompany('name', event.target.value || '')}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <Label htmlFor={`${fieldId}-taxId`}>Steuernummer (optional)</Label>
                                        <Input
                                            id={`${fieldId}-taxId`}
                                            value={accountData.company?.tax_id || ''}
                                            onChange={(event) => updateCompany('tax_id', event.target.value || '')}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex flex-col gap-3">
                                <span className="text-sm font-bold">Geschäftsprofil</span>
                                <div className="flex flex-col gap-1.5">
                                    <Label htmlFor={`${fieldId}-url`}>Website (optional)</Label>
                                    <Input
                                        id={`${fieldId}-url`}
                                        value={accountData.business_profile?.url || ''}
                                        onChange={(event) => updateBusinessProfile('url', event.target.value || '')}
                                    />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <Label htmlFor={`${fieldId}-mcc`}>MCC Code (optional)</Label>
                                    <Input
                                        id={`${fieldId}-mcc`}
                                        value={accountData.business_profile?.mcc || ''}
                                        onChange={(event) => updateBusinessProfile('mcc', event.target.value || '')}
                                        aria-describedby={`${fieldId}-mcc-description`}
                                    />
                                    <p id={`${fieldId}-mcc-description`} className="text-sm text-muted-foreground">
                                        Merchant Category Code für Ihr Geschäft
                                    </p>
                                </div>
                            </div>
                        </div>

                        <DialogFooter className="gap-2 pt-6 sm:gap-0">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowDialog(false)}
                                disabled={loading}
                            >
                                Abbrechen
                            </Button>
                            <Button type="submit" disabled={loading}>
                                {loading && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                                {loading ? 'Wird verarbeitet...' : (isLinkingExisting ? 'Konto verknüpfen' : 'Konto erstellen')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
